import React from "react";
import ReactDOM from "react-dom";
import { fetchQuery, graphql } from "react-relay";

import EventEmitter from "events";
import { cloneDeep } from "lodash/fp";
import { EditorState, Plugin, PluginKey } from "prosemirror-state";
import { commitLocalUpdate } from "relay-runtime";

import { ActionItemIntegrationLinkWidget } from "~/integrations/actionItemIntegration/Widget";
import { Extension } from "~/lib/prose";
import {
	ACTION_ITEM_INTEGRATION_LINK_ANNOTATION_ORDER,
	addAnnotation,
	removeAnnotation,
} from "~/lib/prose/plugins/blockAnnotation";
import { getBlockBoundaryState } from "~/lib/prose/plugins/blockBoundary";
import { environment } from "~/models";

import type { actionItemIntegrationLink$data } from "~/__generated__/actionItemIntegrationLink.graphql";
import type { actionItemIntegrationLinkNode$data as actionItemIntegrationLinkNodeRaw } from "~/__generated__/actionItemIntegrationLinkNode.graphql";
import type { actionItemIntegrationLinkQuery } from "~/__generated__/actionItemIntegrationLinkQuery.graphql";

type ActionItemWithIntegrationLinkInitialData = actionItemIntegrationLink$data["integrationLinkedActionItems"];

export type ActionItemIntegrationLinkNode = Omit<actionItemIntegrationLinkNodeRaw, " $fragmentType">;

export const proseIntegrationKey = "action-item-integration-link-items";
export const refetchMetaKey = "linksRefetched";
export const ActionItemIntegrationLinkPluginKey = new PluginKey<ActionItemIntegrationLinksState>(proseIntegrationKey);

const singleLinkQuery = graphql`
	query actionItemIntegrationLinkQuery($id: ID!) {
		node(id: $id) {
			... on NoteBlock {
				...actionItemIntegrationLinkNoteBlock @relay(mask: false)
			}
		}
	}
`;

function fetchSingleIntegrationLink(id: string) {
	return fetchQuery<actionItemIntegrationLinkQuery>(environment, singleLinkQuery, { id }).toPromise();
}

graphql`
	fragment actionItemIntegrationLinkNode on ActionItemIntegrationLinkNode {
		id
		actionItem {
			originalBlockId
			id
			key
		}
		createdAt
		updatedAt
		syncStatus
		lastSyncedError
		integrationProvider
		integrationData {
			...clickupSpecificData @relay(mask: false)
			...trelloSpecificData @relay(mask: false)
			...mondaySpecificData @relay(mask: false)
			...linearSpecificData @relay(mask: false)
			...microsoftTodoSpecificData @relay(mask: false)
			...hubspotTaskSpecificData @relay(mask: false)
		}
	}
`;

graphql`
	fragment actionItemIntegrationLinkNoteBlock on NoteBlock {
		id
		key
		actionItemIntegrationLink {
			...actionItemIntegrationLinkNode @relay(mask: false)
		}
	}
`;

graphql`
	fragment actionItemIntegrationLink on Note {
		integrationLinkedActionItems: actionItems(integrationConnectedOnly: true) {
			...actionItemIntegrationLinkNoteBlock @relay(mask: false)
		}
	}
`;

export type ActionItemIntegrationLinks = {
	[proseBlockKey: string]: ActionItemIntegrationLinkNode;
};

export type ActionItemIntegrationLinkUpdateData = {
	blockKey: string;
	blockId: string;
};

export type ActionItemIntegrationLinkUpdateDataExpanded = ActionItemIntegrationLinkUpdateData & {
	integrationLink: ActionItemIntegrationLinkNode | null;
};

type ActionItemIntegrationLinksState = {
	integrationLinks: ActionItemIntegrationLinks;
};

export default class ActionItemIntegrationsPlugin extends Extension {
	name = proseIntegrationKey;
	noteId?: string;
	initialData?: ActionItemIntegrationLinks;
	widgetDomNodes = new Map<string, Element>();
	blockDataEmitters = new Map<string, EventEmitter>();

	constructor({ noteId, initialData }: { noteId?: string; initialData?: ActionItemWithIntegrationLinkInitialData }) {
		super();

		this.noteId = noteId;
		this.initialData = {};

		if (!initialData) return;

		for (const { key, actionItemIntegrationLink: actionItemBlockLink } of initialData) {
			if (!this.initialData[key] && actionItemBlockLink) {
				this.initialData[key] = actionItemBlockLink;
			}
		}
	}

	doIntegrationLinkUpdate(
		updateData: ActionItemIntegrationLinkUpdateData,
		actionItemIntegrationLink: ActionItemIntegrationLinkNode | null | undefined,
	) {
		this.updateRelay(updateData.blockId, actionItemIntegrationLink ?? null);

		if (this.editorView) {
			const tr = this.editorView.state.tr;
			tr.setMeta(ActionItemIntegrationLinkPluginKey, {
				...updateData,
				integrationLink: actionItemIntegrationLink,
			} as ActionItemIntegrationLinkUpdateDataExpanded);
			this.editorView.dispatch(tr);
		}
	}

	async dispatchActionItemIntegrationLinkUpdate(updateData: ActionItemIntegrationLinkUpdateData) {
		const integrationLinkData = await fetchSingleIntegrationLink(updateData.blockId);

		const actionItemIntegrationLink = integrationLinkData?.node?.actionItemIntegrationLink;
		this.doIntegrationLinkUpdate(updateData, actionItemIntegrationLink);
	}

	applyUpdateData = (
		oldData: ActionItemIntegrationLinks,
		update: ActionItemIntegrationLinkUpdateDataExpanded,
	): ActionItemIntegrationLinks => {
		const { integrationLink, blockKey } = update;
		const newData = cloneDeep(oldData);

		if (!integrationLink) {
			const widgetDom = this.widgetDomNodes.get(blockKey);
			if (widgetDom) {
				ReactDOM.unmountComponentAtNode(widgetDom);
				if (this.editorView) removeAnnotation(this.editorView, blockKey, widgetDom);
				widgetDom.remove();
			}
			this.widgetDomNodes.delete(blockKey);
			this.blockDataEmitters.delete(blockKey);
			delete newData[blockKey];
		} else {
			newData[blockKey] = integrationLink;
			this.blockDataEmitters.get(blockKey)?.emit("data", newData[blockKey]);
		}
		return newData;
	};

	refreshIntegrationsData = (dataFromQuery: ActionItemWithIntegrationLinkInitialData) => {
		const view = this.editorView;
		if (!view) return;
		const newState: ActionItemIntegrationLinks = {};

		for (const { key, actionItemIntegrationLink: actionItemBlockLink } of dataFromQuery) {
			if (!newState[key] && actionItemBlockLink) {
				newState[key] = actionItemBlockLink;
			}
		}

		view.dispatch(view.state.tr.setMeta(refetchMetaKey, newState));

		this.blockDataEmitters.forEach((emitter, k) => {
			const data = newState[k];
			if (data) {
				emitter.emit("data", data);
			}
		});
	};

	updateRelay = (blockId: string, data: ActionItemIntegrationLinkNode | null) => {
		commitLocalUpdate(environment, store => {
			const noteId = this.noteId;
			if (!noteId) return;
			const noteNode = store.get(noteId);
			const actionItems = noteNode?.getLinkedRecords("actionItems", { integrationConnectedOnly: true })?.slice() ?? [];

			if (actionItems) {
				const newActionItems = actionItems.filter(item => item.getDataID() !== blockId);

				const block = store.get(blockId);
				if (block && data) {
					newActionItems.push(block);
				}

				noteNode?.setLinkedRecords(newActionItems, "actionItems", { integrationConnectedOnly: true });
			}
		});
	};

	updateWidgets = (newState: EditorState, currentPluginState: ActionItemIntegrationLinksState | undefined) => {
		if (!currentPluginState) return;
		const editorView = this.editorView;
		if (!editorView) return;

		const blockBoundaryState = getBlockBoundaryState(newState);
		if (!blockBoundaryState) return;
		const { blockBoundaries } = blockBoundaryState;

		const blockKeysWithLinksRemoved = new Set(
			[...this.widgetDomNodes.keys()].filter(k => !(k in currentPluginState.integrationLinks)),
		);
		blockKeysWithLinksRemoved.forEach(k => {
			let dom = this.widgetDomNodes.get(k);
			if (dom) {
				ReactDOM.unmountComponentAtNode(dom);
				removeAnnotation(editorView, k, dom);
				dom.remove();
			}
			this.widgetDomNodes.delete(k);
			this.blockDataEmitters.delete(k);
		});

		Object.entries(currentPluginState.integrationLinks).forEach(([proseBlockKey, integrationLink]) => {
			if (!(proseBlockKey in blockBoundaries)) return;

			let dom = this.widgetDomNodes.get(proseBlockKey);
			if (!dom) {
				const div = document.createElement("div");
				div.contentEditable = "false";
				dom = div;
				this.widgetDomNodes.set(proseBlockKey, dom);
				let emitter = this.blockDataEmitters.get(proseBlockKey);
				if (!emitter) {
					emitter = new EventEmitter();
					this.blockDataEmitters.set(proseBlockKey, emitter);
				}
				ReactDOM.render(
					<ActionItemIntegrationLinkWidget actionItemLink={integrationLink} integrationLinkSpecificEmitter={emitter} />,
					dom,
				);
				addAnnotation(editorView, proseBlockKey, dom, ACTION_ITEM_INTEGRATION_LINK_ANNOTATION_ORDER);
			}
		});
	};

	get plugins() {
		return [
			new Plugin<ActionItemIntegrationLinksState>({
				key: ActionItemIntegrationLinkPluginKey,
				props: {},
				state: {
					init: (_config, state: EditorState) => {
						if (!this.initialData) return { integrationLinks: {} };
						const value = { integrationLinks: this.initialData };
						this.updateWidgets(state, value);
						return value;
					},
					apply: (tr, value, _oldState, newState) => {
						if (tr.getMeta(ActionItemIntegrationLinkPluginKey)) {
							const newData: ActionItemIntegrationLinkUpdateDataExpanded = tr.getMeta(
								ActionItemIntegrationLinkPluginKey,
							);
							value.integrationLinks = this.applyUpdateData(value.integrationLinks, newData);
						} else if (tr.getMeta(refetchMetaKey)) {
							value.integrationLinks = tr.getMeta(refetchMetaKey);
						}

						this.updateWidgets(newState, value);
						return value;
					},
				},
			}),
		];
	}

	destroy = () => {
		this.widgetDomNodes.forEach(v => {
			ReactDOM.unmountComponentAtNode(v);
			v.remove();
		});
	};
}
